import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { riskApi, OptimizeAllocationRequest, TrainingResponse } from '@/api/risk';

export function usePortfolioSummary() {
  return useQuery({
    queryKey: ['portfolio-summary'],
    queryFn: () => riskApi.getPortfolioSummary(),
    staleTime: 60 * 1000,
  });
}

export function useRankedSites(limit?: number, minTier?: number, maxTier?: number) {
  return useQuery({
    queryKey: ['ranked-sites', limit, minTier, maxTier],
    queryFn: () => riskApi.getRankedSites(limit, minTier, maxTier),
    staleTime: 30 * 1000,
  });
}

export function useSiteRiskHistory(siteId: string, limit?: number) {
  return useQuery({
    queryKey: ['site-risk-history', siteId, limit],
    queryFn: () => riskApi.getSiteRiskHistory(siteId, limit),
    enabled: !!siteId,
  });
}

export function useScoreSite() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (siteId: string) => riskApi.scoreSite(siteId),
    onSuccess: (_, siteId) => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      queryClient.invalidateQueries({ queryKey: ['site', siteId] });
      queryClient.invalidateQueries({ queryKey: ['site-risk-history', siteId] });
      queryClient.invalidateQueries({ queryKey: ['portfolio-summary'] });
      queryClient.invalidateQueries({ queryKey: ['ranked-sites'] });
    },
  });
}

export function useScoreSitesBatch() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (siteIds: string[]) => riskApi.scoreSitesBatch(siteIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      queryClient.invalidateQueries({ queryKey: ['portfolio-summary'] });
      queryClient.invalidateQueries({ queryKey: ['ranked-sites'] });
    },
  });
}

export function useOptimizeAllocation() {
  return useMutation({
    mutationFn: (params: OptimizeAllocationRequest) => riskApi.optimizeAllocation(params),
  });
}

export function useModelInfo() {
  return useQuery({
    queryKey: ['model-info'],
    queryFn: () => riskApi.getModelInfo(),
    staleTime: 5 * 60 * 1000,
  });
}

export function useTrainModel() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params?: {
      min_sites?: number;
      validation_split?: number;
      version_note?: string;
    }) => riskApi.trainModel(params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['model-info'] });
      queryClient.invalidateQueries({ queryKey: ['model-versions'] });
    },
  });
}

export function useModelVersions() {
  return useQuery({
    queryKey: ['model-versions'],
    queryFn: () => riskApi.getModelVersions(),
    staleTime: 60 * 1000,
  });
}
